import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { login, oauthLogin } from '../crud/auth';
import { getWhoami } from '../crud/whoAmI';
import { localStorageKeys } from '../localStorageKeys';
import { useLocalStorage } from './useLocalStorage';

const endpoints = {
  login: '/login',
  oAuthLogin: '/login-oauth'
};

const useAuth = (forceDisableAnalytics) => {
  const { setItem, getItem, removeItem } = useLocalStorage();
  const { i18n } = useTranslation();

  // get global user state to manage user
  const { user, setUser, isLoggedIn, setIsLoggedIn } = useContext(AuthContext);
  const searchParams = new URLSearchParams(window.location.search);

  // Function to update search parameters
  const setSearchParams = (params) => {
    // Update the search parameters
    const newSearchParams = new URLSearchParams(params);

    // Update the URL without reloading the page
    window.history.replaceState(null, '', '?' + newSearchParams.toString());
  };

  // Local auth states
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (data, provider) => {
    // save token in local storage
    setItem(localStorageKeys.token, data.token || data.accessToken);
    // save provider login
    setItem(localStorageKeys.providerLogin, provider || '');
    // hydrate user
    await hydrateUser();
    // set user language
    const userLanguage = getItem(localStorageKeys.language);

    if (userLanguage !== 'null' && userLanguage !== 'undefined')
      i18n.changeLanguage(userLanguage);

    // redirect to dashboard
    setIsLoading(false);
  };

  const hydrateUser = async () => {
    // check for token to hydrate user to avoid unnecessary calls
    if (getItem(localStorageKeys.token) === null) {
      return;
    }

    // get whoAmI
    const whoAmIResponse = await getWhoami();
    // if fail, set user to null
    if (whoAmIResponse?.status === 'error') {
      onLogout({ widthRedirect: false });
      return;
    }

    // check user role. If student, logout and redirect to login of game
    if (whoAmIResponse.role_guid === 'R01') {
      onLogout();
      window.location.href = '/login';
      setIsLoading(false);
      return;
    }

    // save whoAmI
    setItem(localStorageKeys.whoAmI, JSON.stringify(whoAmIResponse));
    setItem(localStorageKeys.userGuid, whoAmIResponse.guid);
    setItem(localStorageKeys.firstName, whoAmIResponse.name);
    setItem(localStorageKeys.lastName, whoAmIResponse.lastname);
    setItem(localStorageKeys.email, whoAmIResponse.email);
    setItem(localStorageKeys.role, whoAmIResponse.role_guid);
    setItem(localStorageKeys.loginTimeStamp, new Date().getTime());
    setItem(localStorageKeys.language, whoAmIResponse.lang_id);
    // setUser
    setUser(whoAmIResponse);
    // set is logged in
    setIsLoggedIn(true);
    setIsLoading(false);
  };

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setItem(localStorageKeys.token, token);
      let tempSearchParams = new URLSearchParams(searchParams);
      tempSearchParams.delete('token');
      setSearchParams(tempSearchParams);
      hydrateUser(); // This ensures it only runs once when the token is found
    } else if (!isLoggedIn && !isLoading) {
      // Check if there's already a user set to avoid unnecessary hydration
      hydrateUser();
    } else if (!user) {
      hydrateUser();
    } else {
      // If there's a user, it means the user is already hydrated
      setIsLoading(false);
    }
    // Omitting searchParams from dependency array to avoid re-triggers due to its changes
    // But be cautious, as this might skip updates if other dependencies change. Adjust according to your needs.
  }, [isLoggedIn]); // Assuming no other dependencies, adjust as needed.

  const onLoginWithEmailAndPassword = async ({ email, password }) => {
    setIsLoading(true);

    if (isLoading) return;

    if (!email || !password) {
      setError('LOGIN_WRONG_FORMAT');
      setIsLoading(false);
      return;
    }

    const loginResponse = await login({ email, password });

    if (loginResponse.status === 'success') {
      const { data } = loginResponse;
      handleLogin(data, 'userpass');
    } else {
      const errorCode = loginResponse?.error?.code || loginResponse?.errorCode;

      if (errorCode && errorCode >= 500) {
        setError('SERVER_ERROR');
      } else {
        setError('LOGIN_ERROR');
      }
      setIsLoading(false);
    }
  };

  const onLoginWithGoogle = async ({ credential }) => {
    const oauthLoginResponse = await oauthLogin({
      identity_provider: 'google',
      token: credential
    });

    if (oauthLoginResponse.status === 'success') {
      const { data } = oauthLoginResponse;
      handleLogin(data, 'google');
    } else {
      setError(
        oauthLoginResponse.data?.error?.errorKey === 'EMAIL_NOT_FOUND'
          ? 'LOGIN_GOOGLE_ERROR_USER_NOT_FOUND'
          : 'LOGIN_GOOGLE_ERROR'
      );
    }
  };

  const onLogout = ({ withRedirect = true }) => {
    // Clear local storage except the production environment flag (if it exists) because it's not related to the user
    const isProductionEnvironment = localStorage.getItem('bb_is_production');
    localStorage.clear();
    isProductionEnvironment &&
      setItem('bb_is_production', isProductionEnvironment); // Keep the production environment flag

    setUser(null);
    // disable redirect to handle logout in the same page in case of login error
    if (withRedirect) {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    if (forceDisableAnalytics) {
      setItem('bb_disable_analytics', 'true');
    }
  }, [forceDisableAnalytics]);

  return {
    user,
    error,
    isLoading,
    isLoggedIn,
    // Functions
    setError,
    onLogout,
    hydrateUser,
    onLoginWithGoogle,
    onLoginWithEmailAndPassword
  };
};

export default useAuth;
